import { useSyncExternalStore } from 'react';

/**
 * Registers the generated service worker (production builds only) and tracks whether a new
 * version is waiting. The new version never takes over on its own: the player chooses when
 * to reload, so code is never swapped underneath a match in progress.
 */
let waiting: ServiceWorker | null = null;
let updateRequested = false;
const listeners = new Set<() => void>();

function setWaiting(worker: ServiceWorker) {
  waiting = worker;
  for (const l of listeners) l();
}

export function registerServiceWorker() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;

  // Reload only when the player asked for the update, not when the first version installs.
  sw.addEventListener('controllerchange', () => {
    if (updateRequested) window.location.reload();
  });

  window.addEventListener('load', () => {
    sw.register('/sw.js')
      .then((registration) => {
        if (registration.waiting && sw.controller) setWaiting(registration.waiting);
        registration.addEventListener('updatefound', () => {
          const incoming = registration.installing;
          incoming?.addEventListener('statechange', () => {
            if (incoming.state === 'installed' && sw.controller) setWaiting(incoming);
          });
        });
        // Check for a new version whenever the player comes back to the tab.
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) void registration.update().catch(() => undefined);
        });
      })
      .catch(() => {
        // No offline support this visit; the app works the same online.
      });
  });
}

export function applyUpdate() {
  if (!waiting) {
    window.location.reload();
    return;
  }
  updateRequested = true;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => waiting !== null,
    () => false,
  );
}
